import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { 
  AlertTriangle, Clock, Mail, Bell, Settings, 
  Loader2, RefreshCw, CheckCircle, Phone, User,
  MessageSquare, XCircle, ExternalLink, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransferAlerts = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [delayedTransfers, setDelayedTransfers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [markingInProgress, setMarkingInProgress] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, transfersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/alerts/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/alerts/delayed-transfers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/alerts/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setDelayedTransfers(transfersRes.data.transfers || []);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching alert data:', error);
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async (updates) => {
    try {
      const response = await axios.put(
        `${API}/admin/alerts/settings`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(response.data);
      toast.success('Paramètres mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSendAlertEmail = async (transferIds = null) => {
    setSendingEmail(true);
    try {
      const response = await axios.post(
        `${API}/admin/alerts/send-email`,
        { transfer_ids: transferIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`Email envoyé pour ${response.data.transfers_reported || 'les'} transfert(s)`);
      } else {
        toast.error(response.data.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de l\'email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMarkInProgress = async (transferId, notes = '') => {
    setMarkingInProgress(transferId);
    try {
      await axios.post(
        `${API}/admin/alerts/mark-in-progress`,
        { transfer_id: transferId, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transfert marqué en cours de traitement');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setMarkingInProgress(null);
    }
  };

  const handleContactSeller = async (transferId) => {
    try {
      const response = await axios.post(
        `${API}/admin/alerts/contact-seller/${transferId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContactInfo(response.data);
      setShowContactDialog('seller');
    } catch (error) {
      toast.error('Erreur lors de la récupération des informations');
    }
  };

  const handleContactBuyer = async (transferId) => {
    try {
      const response = await axios.post(
        `${API}/admin/alerts/contact-buyer/${transferId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContactInfo(response.data);
      setShowContactDialog('buyer');
    } catch (error) {
      toast.error('Erreur lors de la récupération des informations');
    }
  };

  const handleTriggerDailyReport = async () => {
    setSendingEmail(true);
    try {
      const response = await axios.post(
        `${API}/admin/alerts/trigger-daily-report`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`Rapport quotidien envoyé (${response.data.transfers_reported || 0} transferts)`);
      } else {
        toast.info(response.data.message || 'Aucun transfert en retard');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du rapport');
    } finally {
      setSendingEmail(false);
    }
  };

  const getSeverityColor = (hours) => {
    if (hours >= 168) return 'text-red-600 bg-red-50 border-red-200';
    if (hours >= 120) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (hours >= 72) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            Alertes de Transferts
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Transferts dépassant le seuil de {settings?.threshold_hours || 48}h
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowSettings(true)}
            className="gap-2"
          >
            <Settings className="h-4 w-4" />
            Paramètres
          </Button>
          <Button
            onClick={() => handleSendAlertEmail()}
            disabled={sendingEmail || delayedTransfers.length === 0}
            className="gap-2 bg-amber-600 hover:bg-amber-700"
          >
            {sendingEmail ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            Envoyer alerte
          </Button>
          <Button
            onClick={handleTriggerDailyReport}
            disabled={sendingEmail || delayedTransfers.length === 0}
            variant="outline"
            className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            {sendingEmail ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Rapport quotidien
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className={`border-2 ${stats.total_delayed > 0 ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total en retard</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_delayed}</p>
                </div>
                {stats.total_delayed > 0 ? (
                  <AlertTriangle className="h-8 w-8 text-amber-500" />
                ) : (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">2-3 jours</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.by_days?.['2-3 days'] || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">3-5 jours</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.by_days?.['3-5 days'] || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={stats.by_days?.['7+ days'] > 0 ? 'bg-red-50 border-red-200' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">7+ jours</p>
                  <p className="text-2xl font-bold text-red-600">{stats.by_days?.['7+ days'] || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delayed Transfers List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600" />
            Transferts en retard ({delayedTransfers.length})
          </CardTitle>
          <CardDescription>
            Transferts actifs dépassant le délai de traitement normal
          </CardDescription>
        </CardHeader>
        <CardContent>
          {delayedTransfers.length > 0 ? (
            <div className="space-y-4">
              {delayedTransfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className={`border rounded-lg p-4 ${getSeverityColor(transfer.hours_elapsed)} ${transfer.marked_in_progress ? 'opacity-60' : ''}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono font-bold text-lg">{transfer.reference_number}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          transfer.hours_elapsed >= 168 ? 'bg-red-200 text-red-800' :
                          transfer.hours_elapsed >= 72 ? 'bg-amber-200 text-amber-800' :
                          'bg-yellow-200 text-yellow-800'
                        }`}>
                          {transfer.days_elapsed} jours
                        </span>
                        {transfer.marked_in_progress && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-200 text-blue-800">
                            En cours de traitement
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-gray-900 mb-1">{transfer.event_name}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span>Statut: <strong>{transfer.status_label}</strong></span>
                        <span>Vendeur: #{transfer.seller_client_number}</span>
                        <span>Acheteur: #{transfer.buyer_client_number}</span>
                        <span>${transfer.price?.toFixed(2)} ({transfer.ticket_count} billets)</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleContactSeller(transfer.id)}
                        className="gap-1"
                      >
                        <Phone className="h-3 w-3" />
                        Vendeur
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleContactBuyer(transfer.id)}
                        className="gap-1"
                      >
                        <Phone className="h-3 w-3" />
                        Acheteur
                      </Button>
                      {!transfer.marked_in_progress && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkInProgress(transfer.id)}
                          disabled={markingInProgress === transfer.id}
                          className="gap-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                        >
                          {markingInProgress === transfer.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <CheckCircle className="h-3 w-3" />
                          )}
                          En cours
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/transactions/${transfer.id}`, '_blank')}
                        className="gap-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Voir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Aucun transfert en retard</p>
              <p className="text-sm text-gray-500 mt-1">
                Tous les transferts sont dans les délais normaux
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <AlertDialog open={showSettings} onOpenChange={setShowSettings}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Paramètres des alertes
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Seuil d'alerte (heures)</Label>
              <Input
                type="number"
                value={settings?.threshold_hours || 48}
                onChange={(e) => handleUpdateSettings({ threshold_hours: parseInt(e.target.value) })}
                min={1}
                max={720}
              />
              <p className="text-xs text-gray-500">
                Les transferts dépassant ce délai apparaîtront dans les alertes
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Alertes par email</Label>
                <p className="text-xs text-gray-500">Recevoir des notifications par email</p>
              </div>
              <Switch
                checked={settings?.email_alerts_enabled || false}
                onCheckedChange={(checked) => handleUpdateSettings({ email_alerts_enabled: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Alertes dashboard</Label>
                <p className="text-xs text-gray-500">Afficher les alertes dans le dashboard</p>
              </div>
              <Switch
                checked={settings?.dashboard_alerts_enabled || false}
                onCheckedChange={(checked) => handleUpdateSettings({ dashboard_alerts_enabled: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Rapport quotidien</Label>
                <p className="text-xs text-gray-500">Envoyer un résumé chaque matin</p>
              </div>
              <Switch
                checked={settings?.daily_report_enabled || false}
                onCheckedChange={(checked) => handleUpdateSettings({ daily_report_enabled: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label>Emails administrateurs</Label>
              <Input
                value={settings?.admin_emails?.join(', ') || ''}
                onChange={(e) => handleUpdateSettings({ 
                  admin_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                })}
                placeholder="admin@example.com, autre@example.com"
              />
              <p className="text-xs text-gray-500">Séparez les adresses par des virgules</p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Fermer</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Contact Dialog */}
      <AlertDialog open={showContactDialog !== null} onOpenChange={() => setShowContactDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Contacter le {showContactDialog === 'seller' ? 'vendeur' : 'acheteur'}
            </AlertDialogTitle>
          </AlertDialogHeader>
          {contactInfo && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Référence:</span>
                  <span className="font-mono font-bold">{contactInfo.reference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Événement:</span>
                  <span className="font-medium">{contactInfo.event_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Numéro client:</span>
                  <span className="font-mono">#{showContactDialog === 'seller' ? contactInfo.seller_client_number : contactInfo.buyer_client_number}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <User className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Nom</p>
                    <p className="font-medium">{showContactDialog === 'seller' ? contactInfo.seller_name : contactInfo.buyer_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <Mail className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <a 
                      href={`mailto:${showContactDialog === 'seller' ? contactInfo.seller_email : contactInfo.buyer_email}`}
                      className="font-medium text-green-700 hover:underline"
                    >
                      {showContactDialog === 'seller' ? contactInfo.seller_email : contactInfo.buyer_email}
                    </a>
                  </div>
                </div>

                {(contactInfo.seller_phone || contactInfo.buyer_phone) && (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <Phone className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-sm text-gray-600">Téléphone</p>
                      <a 
                        href={`tel:${showContactDialog === 'seller' ? contactInfo.seller_phone : contactInfo.buyer_phone}`}
                        className="font-medium text-amber-700 hover:underline"
                      >
                        {showContactDialog === 'seller' ? contactInfo.seller_phone : contactInfo.buyer_phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Fermer</AlertDialogCancel>
            {contactInfo && (
              <AlertDialogAction
                onClick={() => {
                  window.location.href = `mailto:${showContactDialog === 'seller' ? contactInfo.seller_email : contactInfo.buyer_email}?subject=Transfert ${contactInfo.reference} - Suivi`;
                }}
                className="bg-brand-600"
              >
                <Mail className="h-4 w-4 mr-2" />
                Envoyer un email
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TransferAlerts;
